import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Empty, Select, Spin, Tag } from "antd";
import { searchHosts } from "../../api/host";
import { createHostLookup } from "./hostLookup";

const lookup = createHostLookup(searchHosts);
const toOption = (h) => ({ value: h._id, label: h.roomNumber, host: h });

/**
 * 房间选择（远程搜索）。父组件只需要传 value（房源 id），名称由组件自己取
 *
 * - filters：搜索时的筛选条件对象，原样传给后端，例如 { status: "empty", valid: true }；
 *            值可以是数组（会转成逗号分隔）。不传 = 不筛选，包括已失效、已租出的房源
 * - fields：选中后 option.host 里额外需要的字段（后端有白名单），例如 ["propertyFee"]；不传只有基础字段
 * - 回显已有的 value 时不受 filters 限制，所以已租出/已失效的房间也能显示房号
 */
const HostSelect = forwardRef(function HostSelect(
  {
    value,
    onChange,
    placeholder = "搜索房号",
    disabled = false,
    filters,
    fields,
    onFocus,
    onBlur,
  },
  ref,
) {
  const selectRef = useRef(null);
  useImperativeHandle(ref, () => ({
    focus: () => selectRef.current?.focus(),
    blur: () => selectRef.current?.blur(),
  }));
  const [query, setQuery] = useState("");
  const [hosts, setHosts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const first = useRef(true);
  const wrapRef = useRef(null);

  // filters 常常是内联对象，用序列化结果做依赖，避免每次渲染都重新搜索
  const paramsKey = JSON.stringify([filters || {}, fields || []]);
  const params = useMemo(() => {
    const out = {};
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") return;
      out[k] = Array.isArray(v) ? v.join(",") : v;
    });
    if (fields?.length) out.fields = fields.join(",");
    return out;
  }, [paramsKey]);

  // 房号以数字为主：弹数字键盘（含字母时可在键盘上切换）
  useEffect(() => {
    wrapRef.current
      ?.querySelector("input")
      ?.setAttribute("inputmode", "numeric");
  }, []);

  const load = async (keyword) => {
    const s = ++seq.current; // 慢请求不会覆盖新请求的结果
    setLoading(true);
    try {
      const { data } = await searchHosts({
        ...params,
        keyword: keyword.trim(),
      });
      const list = Array.isArray(data) ? data : [];
      if (s === seq.current) {
        setHosts(list);
        lookup.remember(list);
      }
    } catch {
      if (s === seq.current) setHosts([]);
    } finally {
      if (s === seq.current) setLoading(false);
    }
  };

  // 输入变化后 300ms 再搜索（首次挂载不请求，聚焦时才加载）
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return undefined;
    }
    const timer = setTimeout(() => load(query), 300);
    return () => clearTimeout(timer);
  }, [query, paramsKey]);

  // 有值、但选项里没有对应房间时（比如父组件直接传入 id），按 id 取房源补一个选项
  useEffect(() => {
    if (!value) {
      setSelected(null);
      return undefined;
    }
    if (selected?.value === value || hosts.some((h) => h._id === value))
      return undefined;
    const cached = lookup.peek(value);
    if (cached) {
      setSelected(toOption(cached));
      return undefined;
    }
    let ignore = false;
    lookup.get(value).then((h) => {
      if (!ignore)
        setSelected(h ? toOption(h) : { value, label: "房间已不存在" });
    });
    return () => {
      ignore = true;
    };
  }, [value, selected, hosts]);

  const options = useMemo(() => {
    const list = hosts.map(toOption);
    // 已选房间不在当前搜索结果里时补进选项，Select 才能显示房号
    if (
      selected &&
      selected.value === value &&
      !list.some((o) => o.value === selected.value)
    ) {
      return [selected, ...list];
    }
    return list;
  }, [hosts, selected, value]);

  const handleChange = (next, option) => {
    setSelected(option?.host ? toOption(option.host) : null);
    setQuery("");
    onChange?.(next, option);
  };

  return (
    <div ref={wrapRef} style={{ width: "100%" }}>
      <Select
        ref={selectRef}
        allowClear
        showSearch
        disabled={disabled}
        filterOption={false}
        value={value || undefined}
        searchValue={query}
        placeholder={placeholder}
        options={options}
        optionLabelProp="label"
        optionRender={(option) => {
          const h = option.data.host;
          return (
            <div>
              <strong>{h?.roomNumber ?? option.label}</strong>
              {h?.buildingName && (
                <span style={{ color: "#8c8c8c", marginLeft: 8 }}>
                  {h.buildingName}
                </span>
              )}
              {h?.valid === false && (
                <Tag style={{ marginLeft: 8 }}>已失效</Tag>
              )}
            </div>
          );
        }}
        notFoundContent={
          loading ? (
            <Spin size="small" />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="没有匹配的房间"
            />
          )
        }
        onSearch={setQuery}
        onFocus={(e) => {
          load(query);
          onFocus?.(e);
        }}
        onBlur={onBlur}
        onChange={handleChange}
        style={{ width: "100%" }}
      />
    </div>
  );
});

export default HostSelect;
